import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Briefcase, FileText, Lightbulb, LogOut, Search, Inbox, Users, BarChart3, Database, ShieldCheck, Upload, ClipboardCheck, ListChecks, ClipboardList, Megaphone, UserCog, MessageSquare, Bookmark, Bot, Sparkles, Building2, Radar, FileStack, Handshake, FileSpreadsheet, ScanSearch } from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { ThemeToggle } from './ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_3D = 'https://static.prod-images.emergentagent.com/jobs/9de54d98-d977-4d52-98c6-87def6f7dbb6/images/e4af12b1fd03afcac81e7bb84146d1c3be55c42647b453eb7f18eeb53e861031.png';

// Brand-only: live count of deliverables in `pending_review` across all the
// brand's campaigns. Powers the orange chip on the "My Campaigns" and
// "Approvals" sidebar items. Polls every 30s + listens for an instant-refresh
// event dispatched after the brand approves / requests revision.
const usePendingReviewsCount = (enabled) => {
  const [count, setCount] = useState(0);
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/brand/pending-reviews`, { withCredentials: true });
      setCount(Array.isArray(data) ? data.length : 0);
    } catch {
      // silent — sidebar must never crash on a polling failure
    }
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    load();
    const t = setInterval(load, 30000);
    const onRefresh = () => load();
    window.addEventListener('brand:approvals-refresh', onRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener('brand:approvals-refresh', onRefresh);
    };
  }, [enabled, load]);
  return count;
};

const useHasTracker = (enabled) => {
  const [has, setHas] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/brand-tracker/me`, { withCredentials: true }).then(() => setHas(true)).catch(() => setHas(false));
  }, [enabled]);
  return has;
};

export const Sidebar = () => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const role = user?.role;
  const pendingReviews = usePendingReviewsCount(role === 'brand');
  const hasTracker = useHasTracker(role === 'brand');
  let navItems = [];

  if (role === 'brand') {
    navItems = [
      { path: '/brand/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/brand/discover', label: 'Discover Creators', icon: Search },
      { path: '/brand/shortlists', label: 'Shortlists', icon: ListChecks },
      { path: '/brand/saved-lists', label: 'Saved Lists', icon: Bookmark },
      { path: '/brand/review-lists', label: 'Review Lists', icon: ClipboardCheck },
      { path: '/brand/plans', label: 'Campaign Plans', icon: ClipboardList },
      { path: '/brand/agency-plans', label: 'Agency Plans', icon: FileStack },
      { path: '/brand/campaigns', label: 'My Campaigns', icon: Briefcase, countKey: 'pending_reviews' },
      { path: '/brand/approvals', label: 'Approvals', icon: ClipboardCheck, countKey: 'pending_reviews' },
      ...(hasTracker ? [{ path: '/brand/tracker', label: 'Competitor Tracker', icon: Radar }] : []),
    ];
  } else if (role === 'admin') {
    navItems = [
      { path: '/dashboard', label: 'Overview', icon: LayoutDashboard },
      { path: '/admin/incoming', label: 'Incoming Briefs', icon: Inbox },
      { path: '/campaigns', label: 'Campaigns', icon: Briefcase },
      { path: '/bulk/campaigns', label: 'Bulk Campaigns', icon: Megaphone },
      { path: '/admin/roster', label: 'Creator Roster', icon: Users },
      { path: '/admin/users', label: 'User Management', icon: UserCog },
      { path: '/admin/brand-accounts', label: 'Brand Accounts', icon: Building2 },
      { path: '/admin/agencies', label: 'Agencies', icon: Handshake },
      { path: '/admin/google', label: 'Google Sheets', icon: FileSpreadsheet },
      { path: '/admin/import', label: 'Import Creators', icon: Upload },
      { path: '/invoices', label: 'Invoices', icon: FileText },
      { path: '/admin/platform', label: 'Analytics', icon: BarChart3 },
      { path: '/admin/database', label: 'Infrastructure', icon: Database },
      { path: '/admin/whatsapp', label: 'WhatsApp Logs', icon: MessageSquare },
      { path: '/admin/mandate-questions', label: 'Bot Questions', icon: Bot, section: 'Bot Settings' },
    ];
  } else if (role === 'account_executive') {
    // Account Executive: StrawHat internal — acts on behalf of brands + routes briefs to CMs.
    // Read-only on Roster, Bulk Campaigns, and Review Lists. Hidden from admin platform/DB/import.
    navItems = [
      { path: '/admin/incoming', label: 'Incoming Briefs', icon: Inbox },
      { path: '/ae/campaigns', label: 'All Campaigns', icon: ShieldCheck },
      { path: '/brand/discover', label: 'Discover Creators', icon: Search },
      { path: '/brand/plans', label: 'Campaign Plans', icon: ClipboardList },
      { path: '/bulk/campaigns', label: 'Bulk Campaigns', icon: Megaphone },
      { path: '/cm/review-lists', label: 'Review Lists', icon: ClipboardCheck },
      { path: '/admin/roster', label: 'Creator Roster', icon: Users },
    ];
  } else if (role === 'talent_manager') {
    navItems = [
      { path: '/talent/dashboard', label: 'Talent Cloud', icon: LayoutDashboard },
      { path: '/talent/roster', label: 'My Roster', icon: Users },
      { path: '/talent/opportunities', label: 'Opportunities', icon: Briefcase },
      { path: '/tm/briefs', label: 'Briefs', icon: Inbox },
      { path: '/invoices', label: 'Invoices', icon: FileText },
    ];
  } else if (role === 'agency') {
    navItems = [
      { path: '/agency/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/agency/plans', label: 'Brand Plans', icon: FileStack },
      { path: '/brand/discover', label: 'Discover Creators', icon: Search },
      { path: '/brand/campaigns', label: 'Campaigns', icon: Briefcase },
      { path: '/invoices', label: 'Invoices', icon: FileText },
    ];
  } else if (role === 'campaign_manager' || role === 'manager') {
    navItems = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/campaigns', label: 'My Campaigns', icon: Briefcase },
      { path: '/bulk/campaigns', label: 'Bulk Campaigns', icon: Megaphone },
      { path: '/cm/review-lists', label: 'Review Lists', icon: ClipboardCheck },
      { path: '/invoices', label: 'Invoices', icon: FileText },
      { path: '/content-ideas', label: 'Content Ideas', icon: Lightbulb },
    ];
  } else {
    navItems = [
      { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/campaigns', label: 'My Campaigns', icon: Briefcase },
      { path: '/creator/invites', label: 'Bulk Invites', icon: Megaphone },
      { path: '/invoices', label: 'Invoices', icon: FileText },
      { path: '/content-ideas', label: 'Content Ideas', icon: Lightbulb },
      { path: '/settings/delegation', label: 'Delegation', icon: ShieldCheck },
    ];
  }

  if (['admin', 'manager', 'talent_manager', 'campaign_manager', 'account_executive'].includes(role)) {
    navItems.splice(1, 0, { path: '/ideation', label: 'Brief Suggestions', icon: Sparkles }, { path: '/ideation/saved-briefs', label: 'Saved Briefs', icon: ClipboardList });
  }
  if (['admin', 'account_executive'].includes(role)) {
    navItems.splice(3, 0, { path: '/competitor-analysis', label: 'Competitor Analysis', icon: ScanSearch });
  }

  if (['admin', 'manager', 'talent_manager'].includes(role)) {
    navItems.splice(2, 0, { path: '/creator-ideation', label: 'Ideation', icon: Lightbulb });
  }

  return (
    <div className="w-72 flex flex-col h-screen sticky top-0 relative overflow-hidden transition-colors
      bg-[#F4F4F5] dark:bg-[#0F1011]
      text-[#0F0F0F] dark:text-white
      border-r border-black/[0.08] dark:border-white/[0.06]"
    >
      {/* Glow accent (subtle in light, brighter in dark) */}
      <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full blur-3xl pointer-events-none bg-[#FF451A]/[0.06] dark:bg-[#FF451A]/[0.12]"></div>
      <div className="absolute bottom-40 -right-10 w-40 h-40 rounded-full blur-3xl pointer-events-none bg-[#FF451A]/[0.04] dark:bg-[#FF451A]/[0.10]"></div>

      {/* 3D Logo header */}
      <div className="relative px-6 pt-8 pb-6 border-b border-black/[0.08] dark:border-white/[0.06]">
        <div className="flex items-start justify-between gap-3">
          <motion.div
            initial={{ scale: 0.8, opacity: 0, rotate: -10 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <img src={LOGO_3D} alt="Vyvre" className="w-20 h-20 object-contain drop-shadow-[0_8px_24px_rgba(255,69,26,0.5)] -mt-2 -ml-2" />
          </motion.div>
          <NotificationBell />
        </div>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-2">
          <h1 className="font-display text-3xl leading-none tracking-tight">
            <span className="text-[#0F0F0F] dark:text-white">Vy</span>
            <span className="italic text-[#FF451A]">vre</span>
          </h1>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] mt-1 text-[#A1A1AA] dark:text-[#545861]">Creator Intelligence</div>
        </motion.div>

        {/* User chip */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
          className="mt-5 flex items-center gap-2 p-2.5 rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.08] dark:border-white/[0.06]"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#FF451A] to-[#8A210A] flex items-center justify-center font-bold text-white text-sm shadow-lg shadow-[#FF451A]/30">
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate text-[#0F0F0F] dark:text-white">{user?.name}</div>
            {user?.role && (
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#FF451A]">{user.role.replace('_', ' ')}</div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Nav */}
      <nav className="flex-1 min-h-0 overflow-y-auto p-3 space-y-1 relative z-10">
        {navItems.map((item, i) => {
          const Icon = item.icon;
          const path = location.pathname;
          const isActive =
            path === item.path ||
            (item.path === '/campaigns' && path.startsWith('/campaigns/')) ||
            (item.path === '/admin/roster' && path.startsWith('/creators/')) ||
            (item.path === '/talent/roster' && path.startsWith('/talent/creators/')) ||
            (item.path === '/brand/discover' && path.startsWith('/creators/')) ||
            (item.path === '/brand/discover' && path.startsWith('/brand/creators/')) ||
            (item.path === '/brand/shortlists' && path.startsWith('/brand/shortlists/')) ||
            (item.path === '/brand/plans' && path.startsWith('/brand/plans/')) ||
            (item.path === '/brand/agency-plans' && path.startsWith('/brand/agency-plans/')) ||
            (item.path === '/agency/plans' && path.startsWith('/agency/plans/')) ||
            (item.path === '/admin/agencies' && path.startsWith('/admin/agency-plans/')) ||
            (item.path === '/competitor-analysis' && path.startsWith('/competitor-analysis/')) ||
            (item.path === '/admin/incoming' && path.startsWith('/admin/incoming/'));
          return (
            <motion.div
              key={item.path}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 + i * 0.05, duration: 0.4 }}
            >
              <Link
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 group relative overflow-hidden border-l-2 ${
                  isActive
                    ? 'text-[#0F0F0F] dark:text-white bg-[#FF451A]/[0.06] dark:bg-[#FF451A]/[0.10] border-[#FF451A]'
                    : 'text-[#6B6B6B] dark:text-[#8A8F98] hover:text-[#0F0F0F] dark:hover:text-white hover:bg-black/[0.03] dark:hover:bg-white/[0.03] border-transparent'
                }`}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <Icon size={18} strokeWidth={isActive ? 2 : 1.5} className={isActive ? 'text-[#FF451A]' : ''} />
                <span className="tracking-wide">{item.label}</span>
                {item.countKey === 'pending_reviews' && pendingReviews > 0 && (
                  <span
                    className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF451A] text-white text-[10px] font-bold flex items-center justify-center shadow-[0_0_12px_rgba(255,69,26,0.5)]"
                    data-testid="sidebar-pending-count"
                  >
                    {pendingReviews}
                  </span>
                )}
                {isActive && !(item.countKey === 'pending_reviews' && pendingReviews > 0) && (
                  <motion.span layoutId="active-dot" className="ml-auto w-1.5 h-1.5 rounded-full bg-[#FF451A] shadow-[0_0_10px_#FF451A]"></motion.span>
                )}
              </Link>
            </motion.div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="shrink-0 p-4 space-y-2 relative z-10 border-t border-black/[0.08] dark:border-white/[0.06]">
        {/* Theme toggle sits above the search/logout stack */}
        <div className="flex items-center justify-between gap-2 pb-1">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] dark:text-[#545861]">Theme</span>
          <ThemeToggle />
        </div>
        <button
          onClick={() => {
            const evt = new KeyboardEvent('keydown', { key: 'k', metaKey: true });
            window.dispatchEvent(evt);
          }}
          className="w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-xs transition-colors
            text-[#6B6B6B] dark:text-[#8A8F98]
            border border-black/[0.08] dark:border-white/[0.06]
            hover:text-[#0F0F0F] dark:hover:text-white
            hover:bg-black/[0.03] dark:hover:bg-white/[0.03]"
          data-testid="open-command-palette"
        >
          <span className="flex items-center gap-2"><Search size={12} /> Search…</span>
          <kbd className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-black/[0.05] dark:bg-white/[0.05] text-[#A1A1AA] dark:text-[#545861]">⌘K</kbd>
        </button>
        <button
          onClick={logout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors text-sm font-medium
            text-[#6B6B6B] dark:text-[#8A8F98]
            hover:text-[#FF451A] hover:bg-[#FF451A]/[0.06] dark:hover:bg-[#FF451A]/[0.10]"
          data-testid="logout-button"
        >
          <LogOut size={16} strokeWidth={1.5} />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
};
